import { Button, TextField } from '@execonline-inc/execonline-ui';
import { useQueryClient } from '@tanstack/react-query';
import { just, nothing } from 'maybeasy';
import { observer } from 'mobx-react';
import * as React from 'react';
import { NotTranslated, T, TPlainTextKey } from '../../../../Translations';
import { serverErrorMessage, submitMetadata } from '../../Api';
import { ConnectionResource, SsoVendor, SubmitFailure } from '../../Types';

interface Props {
  resource: ConnectionResource;
}

type State =
  | { kind: 'editing'; metadataUrl: string; error: SubmitFailure }
  | { kind: 'verifying'; metadataUrl: string };

const metadataInstruction = (vendor: SsoVendor): TPlainTextKey => {
  switch (vendor) {
    case 'entra':
      return 'When your Entra ID application is saved, copy its App Federation Metadata URL (found under SAML Certificates) and paste it here.';
    case 'okta':
    case 'adfs':
    case 'ping':
    case 'other':
      return 'When your identity provider application is saved, copy its federation metadata URL and paste it here.';
  }
};

const FinishConnectionCard: React.FC<Props> = ({ resource }) => {
  const [state, setState] = React.useState<State>({
    kind: 'editing',
    metadataUrl: resource.payload.metadataUrl.getOrElseValue(''),
    error: nothing(),
  });
  const queryClient = useQueryClient();

  const verify = () => {
    if (state.kind !== 'editing') {
      return;
    }
    const { metadataUrl } = state;
    setState({ kind: 'verifying', metadataUrl });
    submitMetadata(resource.links, metadataUrl.trim()).fork(
      (err) => setState({ kind: 'editing', metadataUrl, error: just(serverErrorMessage(err)) }),
      () => {
        setState({ kind: 'editing', metadataUrl, error: nothing() });
        queryClient.invalidateQueries({ queryKey: ['sso-configuration'] });
        queryClient.invalidateQueries({ queryKey: ['sso-configurations'] });
      },
    );
  };

  return (
    <div>
      <h3 className="text-base font-semibold text-foreground">
        <T kind="Finish the connection" />
      </h3>
      <div className="mt-3 rounded-lg border border-border bg-card p-6">
        <p className="text-sm text-muted-foreground">
          <T kind={metadataInstruction(resource.payload.vendor)} />
        </p>
        <div className="mt-4">
          <TextField
            label={<T kind="Metadata URL" />}
            value={state.metadataUrl}
            onChange={(event) =>
              state.kind === 'editing' && setState({ ...state, metadataUrl: event.target.value })
            }
            placeholder="https://"
            helperText={
              <T kind="Prefer a URL - we will pick up certificate rollovers automatically." />
            }
            inputClassName="border-none"
            fullWidth
            data-intercom-target="sso-metadata-url-input-field"
          />
        </div>
        {state.kind === 'editing' &&
          state.error
            .map((message) => (
              <p key="error" className="mt-3 text-sm text-destructive">
                {message
                  .map((text) => <NotTranslated key="server" text={text} />)
                  .getOrElse(() => (
                    <T kind="We are unable to process your request at this time" />
                  ))}
              </p>
            ))
            .getOrElse(() => <></>)}
        <div className="mt-4">
          <Button
            color="success"
            fullWidth
            isLoading={state.kind === 'verifying'}
            disabled={state.metadataUrl.trim().length === 0 || state.kind === 'verifying'}
            onClick={verify}
            data-intercom-target="sso-verify-connection-button"
          >
            <T kind="Verify connection" />
          </Button>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          <T kind="We will validate the metadata, then take you to a test sign-in that checks the attributes your IdP sends. No one is affected until you assign a domain to this connection." />
        </p>
      </div>
    </div>
  );
};

export default observer(FinishConnectionCard);
